import { Gender } from "../../models/Actor";
import { Outfit } from "../../models/Outfit";

/**
 * Where the picker was opened from. It decides the first tile:
 * - `take`: "Default" — no outfit change on this take; the actor keeps their own outfit.
 * - `preset`: "Random" — the actor preset has no fixed outfit (the old shuffle button).
 * - `setOutfit`: no special tile; the interaction always names an outfit.
 * In the data both specials are the empty outfit string.
 */
export type OutfitPickerContext = "take" | "preset" | "setOutfit";

export type OutfitPickerItem =
  | { kind: "default"; key: string }
  | { kind: "random"; key: string }
  | { kind: "outfit"; key: string; outfit: Outfit };

/** Gender the renders are previewed in: the actor's, or male for a random actor. */
export type PreviewGender = Gender.male | Gender.female;

export const outfitItem = (outfit: Outfit, block: string): OutfitPickerItem => ({
  kind: "outfit",
  key: `${block}:${outfit.name}`,
  outfit,
});

export function specialItem(context: OutfitPickerContext): OutfitPickerItem | undefined {
  if (context === "take") return { kind: "default", key: "special:default" };
  if (context === "preset") return { kind: "random", key: "special:random" };
  return undefined;
}

/** The value an item stands for in the training data. */
export const itemValue = (item: OutfitPickerItem) =>
  item.kind === "outfit" ? item.outfit.name : "";

export const initialPreviewGender = (gender: Gender | undefined): PreviewGender =>
  gender === Gender.female ? Gender.female : Gender.male;

/**
 * Arrow-key geometry for two stacked grids sharing one index range: the
 * "In this training" block (`topCount` items) above the catalog. ↑ ↓ step a
 * row inside a block and cross into the other block at the same column.
 */
export function moveInBlocks(
  index: number,
  direction: "up" | "down" | "left" | "right",
  topCount: number,
  total: number,
  columns: number
): number {
  if (total === 0) return 0;
  const clamp = (i: number) => Math.min(Math.max(i, 0), total - 1);
  if (direction === "left") return clamp(index - 1);
  if (direction === "right") return clamp(index + 1);

  const inTop = index < topCount;
  const blockStart = inTop ? 0 : topCount;
  const blockCount = inTop ? topCount : total - topCount;
  const local = index - blockStart;
  const column = local % columns;

  if (direction === "down") {
    if (local + columns < blockCount) return index + columns;
    if (inTop && total > topCount) return topCount + Math.min(column, total - topCount - 1);
    return index;
  }

  if (local - columns >= 0) return index - columns;
  if (!inTop && topCount > 0) {
    const lastRowStart = Math.floor((topCount - 1) / columns) * columns;
    return Math.min(lastRowStart + column, topCount - 1);
  }
  return index;
}
